import { Routes, Route, Navigate } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { LoginPage, RegisterPage } from '@/features/auth';
import {
  OnboardingPage,
  Step2,
  Step3,
  Step4,
  Step5Placeholder,
  OnboardingSuccessPage,
  OnboardingLayout,
} from '@/features/onboarding';
import { TreeDashboardPage } from '@/features/dashboard';
import { OnboardingSessionProvider, useOnboardingSession } from '@/contexts/OnboardingContext';
import { useAuth } from '@/contexts/AuthContext';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import type { ReactNode } from 'react';

/**
 * Route Guard: Requires onboarding to be complete
 * Redirects to onboarding if incomplete
 */
function RequireOnboarding({ children }: { children: ReactNode }) {
  const { user, userProfile, isAuthReady, profileResolutionState } = useAuth();

  console.log('[RequireOnboarding] Guard check:', {
    profileResolutionState,
    isAuthReady,
    hasUser: !!user,
    hasProfile: !!userProfile,
    onboardingCompletedAt: userProfile?.onboarding_completed_at,
  });

  // CRITICAL: Wait for auth resolution to complete
  // This prevents routing decisions with incomplete auth state
  if (!isAuthReady) {
    console.log('[RequireOnboarding] WAITING - Auth not ready', {
      profileResolutionState,
      isAuthReady,
    });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('[RequireOnboarding] No user, redirecting to login');
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Now safe to check profile - auth resolution is complete
  // userProfile comes from AuthContext resolvedProfile state (single source of truth)
  if (!userProfile?.onboarding_completed_at) {
    console.log('[RequireOnboarding] Onboarding not completed, redirecting to onboarding', {
      hasProfile: !!userProfile,
    });
    return <Navigate to={ROUTES.ONBOARDING} replace />;
  }

  console.log('[RequireOnboarding] Onboarding completed, allowing access to dashboard');
  return <>{children}</>;
}

/**
 * Route Guard: Basic auth check for onboarding routes
 * Prevents access if onboarding is already fully complete
 */
function OnboardingAuthGuard({ children }: { children: ReactNode }) {
  const { user, userProfile, isAuthReady, profileResolutionState } = useAuth();

  console.log('[OnboardingAuthGuard] Guard check:', {
    profileResolutionState,
    isAuthReady,
    hasUser: !!user,
    hasProfile: !!userProfile,
    onboardingCompletedAt: userProfile?.onboarding_completed_at,
  });

  // CRITICAL: Wait for auth resolution to complete
  // This prevents routing decisions with incomplete auth state
  if (!isAuthReady) {
    console.log('[OnboardingAuthGuard] WAITING - Auth not ready', {
      profileResolutionState,
      isAuthReady,
    });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('[OnboardingAuthGuard] No user, redirecting to login');
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Now safe to check profile - auth resolution is complete
  // userProfile comes from AuthContext resolvedProfile state (single source of truth)
  if (userProfile?.onboarding_completed_at) {
    console.log('[OnboardingAuthGuard] Onboarding already completed, redirecting to dashboard');
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  console.log('[OnboardingAuthGuard] Onboarding not completed, allowing access to onboarding flow', {
    hasProfile: !!userProfile,
  });
  return <>{children}</>;
}

/**
 * Route Guard: Session-based routing for onboarding flow
 * Must be used INSIDE OnboardingSessionProvider
 *
 * State Logic:
 * - ready_to_finalize (completion_ready_at != null, completed_at == null): Only allow /success
 * - in_progress (completion_ready_at == null): Allow step navigation
 */
function OnboardingSessionGuard({
  children,
  isSuccessPage = false
}: {
  children: ReactNode;
  isSuccessPage?: boolean;
}) {
  const { session, isLoadingSession } = useOnboardingSession();

  if (isLoadingSession) {
    const timestamp = new Date().toISOString();
    console.log(`[OnboardingSessionGuard] ${timestamp} - RENDERING LOADING SPINNER`, {
      isLoadingSession,
      hasSession: !!session,
      isSuccessPage
    });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading... (OnboardingSessionGuard)</p>
        </div>
      </div>
    );
  }

  // Check session state for routing logic
  const completionReadyAt = session?.completion_ready_at;
  const completedAt = session?.completed_at;

  // State: ready_to_finalize (Step 4 completed, waiting for tree creation)
  if (completionReadyAt && !completedAt) {
    // User is in ready_to_finalize state - MUST be on success page
    if (!isSuccessPage) {
      return <Navigate to={ROUTES.ONBOARDING_SUCCESS} replace />;
    }
  }

  // State: in_progress (still working through steps)
  // If user tries to access success page before completing Step 4, redirect to current step
  if (!completionReadyAt && isSuccessPage) {
    const nextStep = session?.current_step || 1;
    const targetRoute = nextStep === 1 ? ROUTES.ONBOARDING : `${ROUTES.ONBOARDING}/step-${nextStep}`;
    return <Navigate to={targetRoute} replace />;
  }

  return <>{children}</>;
}

/**
 * Route Guard: Redirects authenticated users based on onboarding state
 * For login/register pages that should only be accessible when not authenticated
 *
 * Note: This guard does NOT use OnboardingSessionProvider context to avoid circular dependencies.
 * Instead, it makes a simple redirect decision:
 * - If onboarding_completed_at exists: go to dashboard
 * - Otherwise: go to /onboarding (which will then route to correct step/success page)
 */
function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { user, userProfile, isAuthReady, profileResolutionState } = useAuth();

  // CRITICAL: Wait for auth resolution to complete
  // This prevents routing decisions with incomplete auth state
  // Without this, users who completed onboarding get incorrectly redirected to /onboarding
  if (!isAuthReady) {
    console.log('[RedirectIfAuthenticated] WAITING - Auth not ready', {
      profileResolutionState,
      isAuthReady,
    });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading session...</p>
        </div>
      </div>
    );
  }

  if (user) {
    // If authenticated, redirect based on onboarding status (safe now, auth resolution complete)
    // userProfile comes from AuthContext resolvedProfile state (single source of truth)
    if (userProfile?.onboarding_completed_at) {
      console.log('[RedirectIfAuthenticated] Authenticated with completed onboarding, redirecting to dashboard');
      return <Navigate to={ROUTES.DASHBOARD} replace />;
    } else {
      console.log('[RedirectIfAuthenticated] Authenticated without completed onboarding, redirecting to onboarding');
      // Let the onboarding flow handle routing to correct step/success page
      return <Navigate to={ROUTES.ONBOARDING} replace />;
    }
  }

  return <>{children}</>;
}

export function AppRouter() {
  const { user, userProfile, isAuthReady, profileResolutionState } = useAuth();

  // CRITICAL: Block all routing until auth resolution is complete
  // This prevents routing decisions with incomplete auth state
  // isAuthReady = true only when authStatus !== 'loading'
  if (!isAuthReady) {
    console.log('[AppRouter] WAITING FOR AUTH RESOLUTION', {
      profileResolutionState,
      isAuthReady,
    });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading session...</p>
        </div>
      </div>
    );
  }

  // App can now render - auth resolution complete (with or without profile)
  // userProfile comes from AuthContext resolvedProfile state (single source of truth)
  console.log('[AppRouter] AUTH RESOLVED - RENDERING ROUTES', {
    profileResolutionState,
    isAuthReady,
    hasUser: !!user,
    hasProfile: !!userProfile,
    onboardingCompletedAt: userProfile?.onboarding_completed_at
  });

  return (
    <Routes>
      {/* Home Route - Redirect based on auth status */}
      {/* Note: Let /onboarding handle state-based routing (step vs success) */}
      <Route
        path={ROUTES.HOME}
        element={
          user ? (
            userProfile?.onboarding_completed_at ? (
              <Navigate to={ROUTES.DASHBOARD} replace />
            ) : (
              <Navigate to={ROUTES.ONBOARDING} replace />
            )
          ) : (
            <Navigate to={ROUTES.LOGIN} replace />
          )
        }
      />

      {/* Auth Routes - Only accessible when not authenticated */}
      <Route
        path={ROUTES.LOGIN}
        element={
          <ErrorBoundary>
            <RedirectIfAuthenticated>
              <LoginPage />
            </RedirectIfAuthenticated>
          </ErrorBoundary>
        }
      />
      <Route
        path={ROUTES.REGISTER}
        element={
          <ErrorBoundary>
            <RedirectIfAuthenticated>
              <RegisterPage />
            </RedirectIfAuthenticated>
          </ErrorBoundary>
        }
      />

      {/* Onboarding Routes - Require auth but block if already completed */}
      {/* Structure: ErrorBoundary > OnboardingAuthGuard > OnboardingSessionProvider > OnboardingSessionGuard > Page */}
      <Route
        path={ROUTES.ONBOARDING}
        element={
          <ErrorBoundary>
            <OnboardingAuthGuard>
              <OnboardingSessionProvider>
                <OnboardingPage />
              </OnboardingSessionProvider>
            </OnboardingAuthGuard>
          </ErrorBoundary>
        }
      />
      <Route
        path={ROUTES.ONBOARDING_STEP_2}
        element={
          <ErrorBoundary>
            <OnboardingAuthGuard>
              <OnboardingSessionProvider>
                <OnboardingSessionGuard>
                  <OnboardingLayout>
                    <Step2 />
                  </OnboardingLayout>
                </OnboardingSessionGuard>
              </OnboardingSessionProvider>
            </OnboardingAuthGuard>
          </ErrorBoundary>
        }
      />
      <Route
        path={ROUTES.ONBOARDING_STEP_3}
        element={
          <ErrorBoundary>
            <OnboardingAuthGuard>
              <OnboardingSessionProvider>
                <OnboardingSessionGuard>
                  <OnboardingLayout>
                    <Step3 />
                  </OnboardingLayout>
                </OnboardingSessionGuard>
              </OnboardingSessionProvider>
            </OnboardingAuthGuard>
          </ErrorBoundary>
        }
      />
      <Route
        path={ROUTES.ONBOARDING_STEP_4}
        element={
          <ErrorBoundary>
            <OnboardingAuthGuard>
              <OnboardingSessionProvider>
                <OnboardingSessionGuard>
                  <OnboardingLayout>
                    <Step4 />
                  </OnboardingLayout>
                </OnboardingSessionGuard>
              </OnboardingSessionProvider>
            </OnboardingAuthGuard>
          </ErrorBoundary>
        }
      />
      <Route
        path={ROUTES.ONBOARDING_STEP_5}
        element={
          <ErrorBoundary>
            <OnboardingAuthGuard>
              <OnboardingSessionProvider>
                <OnboardingSessionGuard>
                  <OnboardingLayout>
                    <Step5Placeholder />
                  </OnboardingLayout>
                </OnboardingSessionGuard>
              </OnboardingSessionProvider>
            </OnboardingAuthGuard>
          </ErrorBoundary>
        }
      />
      <Route
        path={ROUTES.ONBOARDING_SUCCESS}
        element={
          <ErrorBoundary>
            <OnboardingAuthGuard>
              <OnboardingSessionProvider>
                <OnboardingSessionGuard isSuccessPage={true}>
                  <OnboardingLayout>
                    <OnboardingSuccessPage />
                  </OnboardingLayout>
                </OnboardingSessionGuard>
              </OnboardingSessionProvider>
            </OnboardingAuthGuard>
          </ErrorBoundary>
        }
      />

      {/* Protected Routes - Require auth and completed onboarding */}
      <Route
        path={ROUTES.DASHBOARD}
        element={
          <ErrorBoundary>
            <RequireOnboarding>
              <TreeDashboardPage />
            </RequireOnboarding>
          </ErrorBoundary>
        }
      />

      {/* Add more routes as features are implemented */}
    </Routes>
  );
}
